import React, { useState, useEffect, useMemo } from "react";
import { Plus, Eye, Edit, Trash2, FileText, History } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import TechnicalFileForm from "./TechnicalFileForm";
import RecipeVersionHistoryModal from "./RecipeVersionHistoryModal";
import Button from "../Common/Button";
import { SearchInput, SummaryStrip, ListLoader, ListEmpty, ListCard, RowActions, thClass } from "../../stock/StockUI";
import { formatCurrency, MarginBadge, SortTh, tdClass, ErrorLine, useCreateRequest } from "./technicalFilesUI";

const TechnicalFilesList = ({ isDarkMode, createRequestKey = 0 }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [historyFile, setHistoryFile] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState('create'); // 'create' | 'edit' | 'view'
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
    const queryClient = useQueryClient();

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch technical files
    const fetchTechnicalFiles = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            let url = '/recipes';
            const params = [];

            if (debouncedSearchTerm) {
                params.push(`search=${encodeURIComponent(debouncedSearchTerm)}`);
            }

            // Filter only main recipes (not sub-recipes)
            params.push('is_sub_recipe=false');

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Erreur lors de la récupération des fiches techniques";
            throw new Error(errorMessage);
        }
    };

    const { data: filesData, isLoading, error } = useQuery({
        queryKey: ['technical-files', debouncedSearchTerm],
        queryFn: fetchTechnicalFiles,
        keepPreviousData: true
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (id) => {
            const accountId = await secureStorage.getItem("selectedAccount");
            return api.delete(`/recipes/${id}`, {
                headers: {
                    'X-account-id': accountId.id,
                }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['technical-files']);
        }
    });

    // Sort files
    const sortedFiles = useMemo(() => {
        if (!filesData?.data) return [];

        const sortableFiles = [...filesData.data];
        sortableFiles.sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
        return sortableFiles;
    }, [filesData, sortConfig]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handleCreate = () => {
        setFormMode('create');
        setSelectedFile(null);
        setShowForm(true);
    };

    const handleView = (file) => {
        setFormMode('view');
        setSelectedFile(file);
        setShowForm(true);
    };

    const handleEdit = (file) => {
        setFormMode('edit');
        setSelectedFile(file);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette fiche technique ?')) {
            try {
                await deleteMutation.mutateAsync(id);
            } catch (error) {
                alert('Erreur lors de la suppression');
            }
        }
    };

    useCreateRequest(createRequestKey, handleCreate);

    const count = sortedFiles.length;
    const avg = (field) => count ? sortedFiles.reduce((acc, file) => acc + (file[field] || 0), 0) / count : 0;

    const rowActions = (file) => [
        { label: 'Voir', icon: Eye, onClick: () => handleView(file) },
        { label: 'Modifier', icon: Edit, onClick: () => handleEdit(file) },
        { label: 'Historique des versions', icon: History, onClick: () => setHistoryFile(file) },
        { divider: true },
        { label: 'Supprimer', icon: Trash2, danger: true, onClick: () => handleDelete(file.id) },
    ];

    return (
        <div className="text-sm">
            <div className="flex flex-col sm:flex-row">
                <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher une fiche technique..." />
            </div>

            {/* Totaux : bande à filets, comme le Stock */}
            <SummaryStrip items={[
                { key: 'total', label: 'Total fiches', value: count },
                { key: 'cost', label: 'Coût moyen', value: `${formatCurrency(avg('cost'))} DH`, color: 'text-primary-dark dark:text-primary' },
                { key: 'margin', label: 'Marge moyenne', value: `${formatCurrency(avg('margin'))}%` },
                { key: 'time', label: 'Temps moyen', value: `${Math.round(avg('preparationTime'))} min` },
            ]} />

            {isLoading ? (
                <ListLoader label="Chargement des fiches techniques..." />
            ) : error ? (
                <ErrorLine message={error.message} />
            ) : (
                <ListCard>
                    {count === 0 ? (
                        <ListEmpty
                            icon={FileText}
                            title={debouncedSearchTerm ? 'Aucune fiche trouvée' : 'Aucune fiche technique'}
                            text={debouncedSearchTerm
                                ? "Aucune fiche technique ne correspond à votre recherche."
                                : "Aucune fiche technique disponible."}
                            action={!debouncedSearchTerm && (
                                <Button variant="tinted" size="sm" icon={Plus} className="mt-4" onClick={handleCreate}>
                                    Nouvelle fiche
                                </Button>
                            )}
                        />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <SortTh label="Nom" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                                    <th className={`${thClass} text-center`}>Version</th>
                                    <SortTh label="Coût matière" sortKey="cost" sortConfig={sortConfig} onSort={requestSort} align="right" />
                                    <SortTh label="Marge" sortKey="margin" sortConfig={sortConfig} onSort={requestSort} align="right" />
                                    <th className={`${thClass} text-right`}>Temps prépa.</th>
                                    <th className={thClass}><span className="sr-only">Actions</span></th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {sortedFiles.map((file) => (
                                    <tr key={file.id} onClick={() => handleView(file)}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer">
                                        <td className={`${tdClass} font-semibold text-gray-900 dark:text-gray-100`}>{file.name}</td>
                                        <td className={`${tdClass} text-center`}>
                                            <span className="px-2 py-0.5 text-xs font-semibold bg-primary/10 text-primary-dark dark:text-primary rounded-full">
                                                v{file.version || 1}
                                            </span>
                                        </td>
                                        <td className={`${tdClass} text-right font-semibold tabular-nums text-gray-900 dark:text-gray-100`}>
                                            {formatCurrency(file.cost)} DH
                                        </td>
                                        <td className={`${tdClass} text-right`}><MarginBadge value={file.margin} /></td>
                                        <td className={`${tdClass} text-right tabular-nums text-gray-700 dark:text-gray-300`}>
                                            {file.preparationTime} min
                                        </td>
                                        <td className={`${tdClass} text-right`} onClick={(e) => e.stopPropagation()}>
                                            <RowActions actions={rowActions(file)} />
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ListCard>
            )}

            {showForm && (
                <TechnicalFileForm
                    mode={formMode}
                    file={selectedFile}
                    onClose={() => {
                        setShowForm(false);
                        setSelectedFile(null);
                    }}
                    isDarkMode={isDarkMode}
                />
            )}

            {historyFile && (
                <RecipeVersionHistoryModal
                    recipeId={historyFile.id}
                    recipeName={historyFile.name}
                    isDarkMode={isDarkMode}
                    onClose={() => setHistoryFile(null)}
                />
            )}
        </div>
    );
};

export default TechnicalFilesList;
